import { AppConfig } from "./AppConfig.js";
import { ApiCache } from "./ApiCache.js";
import { ApiError } from "./ApiError.js";

export { ApiError };

// El unico modulo que hace fetch. Estatico, sin instancias.
// Adaptaciones al patron ducode:
//  - los errores HTTP (4xx/5xx) NO se reintentan: se lanzan como ApiError;
//    el retry con backoff queda solo para fallos de red (fetch rechazado).
//  - sin header de auth: la API no usa JWT, el identificador (CodigoLegajo)
//    viaja en la URL/body como lo espera el backend.
// Con VITE_USE_MOCK=true (npm run dev:mock) responde MockService en vez de la API.
// Opciones por request: { cacheTiempo: "5m", cachePersistent: true, forceRefresh: false, dedupe: true }.
export class ApiService {
  static baseUrl = AppConfig.api.isLocal ? AppConfig.api.localUrl : AppConfig.api.baseUrl;
  static retryAttempts = AppConfig.api.retryAttempts;

  static async get(endpoint, options = {}) {
    if (AppConfig.api.useMock) return this.mock("GET", endpoint);
    return this.makeRequest("GET", this.baseUrl + "/" + endpoint, null, options);
  }

  static async post(endpoint, data, options = {}) {
    if (AppConfig.api.useMock) return this.mock("POST", endpoint, data);
    return this.makeRequest("POST", this.baseUrl + "/" + endpoint, data, options);
  }

  static async put(endpoint, data, options = {}) {
    if (AppConfig.api.useMock) return this.mock("PUT", endpoint, data);
    return this.makeRequest("PUT", this.baseUrl + "/" + endpoint, data, options);
  }

  static async delete(endpoint, options = {}) {
    if (AppConfig.api.useMock) return this.mock("DELETE", endpoint);
    return this.makeRequest("DELETE", this.baseUrl + "/" + endpoint, null, options);
  }

  // Import dinamico: el mock (y su algoritmo de habilitacion) queda en un chunk aparte
  // que solo se descarga en modo mock, nunca en produccion.
  static async mock(method, endpoint, data = null) {
    const { MockService } = await import("../services/MockService.js");
    return MockService.request(method, endpoint, data);
  }

  // Tras un POST/DELETE que muta datos: ApiService.invalidate("Vacaciones")
  static invalidate(endpointContains = "") {
    ApiCache.invalidate({ urlContains: this.baseUrl + "/" + endpointContains });
  }

  static async makeRequest(method, url, data = null, options = {}) {
    const {
      cacheTiempo = false,
      cachePersistent = true,
      forceRefresh = false,
      dedupe = true,
    } = options;

    const cache = AppConfig.api.cacheEnabled ? cacheTiempo : false;
    const ttlMs = ApiCache.parseTTL(cache);
    const cacheKey = ApiCache.buildKey({ method, url, data });
    const canCache = ApiCache.canCache({ ttlMs, data });

    if (canCache && !forceRefresh) {
      const cached = ApiCache.read(cacheKey);
      if (cached !== null) return cached;
    }

    if (dedupe) {
      const inflight = ApiCache.getInFlight(cacheKey);
      if (inflight) return inflight;
    }

    const requestPromise = (async () => {
      let lastError;
      for (let attempt = 1; attempt <= this.retryAttempts; attempt++) {
        let response;
        try {
          const headers = {};
          let bodyToSend = data;
          if (data && !(data instanceof FormData)) {
            headers["Content-Type"] = "application/json";
            bodyToSend = JSON.stringify(data);
          }
          response = await fetch(url, {
            method,
            headers,
            body: bodyToSend || null,
            signal: AbortSignal.timeout(AppConfig.api.timeout),
          });
        } catch (error) {
          // Fallo de red / timeout: se reintenta con backoff lineal
          lastError = error;
          if (AppConfig.features?.debug) console.warn("Intento " + attempt + " fallo:", error);
          if (attempt === this.retryAttempts) break;
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          continue;
        }

        if (!response.ok) {
          throw new ApiError(response.status, await this.leerBody(response));
        }

        const json = await this.leerBody(response);
        if (canCache) ApiCache.write(cacheKey, json, ttlMs, cachePersistent);
        return json;
      }
      console.error("API Error tras todos los reintentos:", lastError);
      throw lastError;
    })();

    if (dedupe) ApiCache.setInFlight(cacheKey, requestPromise);
    try {
      return await requestPromise;
    } finally {
      if (dedupe) ApiCache.clearInFlight(cacheKey);
    }
  }

  // El backend responde a veces sin cuerpo (Ok() vacio): no forzar el JSON.
  static async leerBody(response) {
    const text = await response.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
}
